import React from 'react';
import { Check } from 'lucide-react';
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import UpgradeButton from './common/UpgradeButton';
import { useUser } from '../contexts/UserContext';

export default function Membership() {
  const { isPremium, isAdmin } = useUser();

  const features = [
    {
      name: 'Write and save bits',
      free: true,
      premium: true,
    },
    {
      name: 'Basic bit organization',
      free: true,
      premium: true,
    },
    {
      name: 'Analytics dashboard',
      free: true,
      premium: true,
    },
    {
      name: 'Create up to 10 bits total',
      free: true,
      premium: false,
      freeOnly: true
    },
    {
      name: 'Create up to 10 ideas total',
      free: true,
      premium: false,
      freeOnly: true
    },
    {
      name: 'Bit Feedback analyzer (3 free uses)',
      free: true,
      premium: false,
      freeOnly: true
    },
    {
      name: 'Unlimited bits',
      free: false,
      premium: true,
    },
    {
      name: 'Unlimited ideas',
      free: false,
      premium: true,
    },
    {
      name: 'Unlimited Bit Feedback analyzer',
      free: false,
      premium: true,
    },
    {
      name: 'Unlimited Voice Recording for Bits/Ideas',
      free: false,
      premium: true,
    },
    {
      name: 'Unlimited characters per bit/idea',
      free: false,
      premium: true,
    }
  ];

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your premium membership? You'll lose access to premium features at the end of your billing period.")) {
      return;
    }

    const functions = getFunctions();
    const cancelSubscription = httpsCallable(functions, "cancelSubscription");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      alert("You must be logged in to cancel.");
      return;
    }

    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const subscriptionId = userDoc.data()?.subscriptionId;

      if (!subscriptionId) {
        alert("No active subscription found.");
        return;
      }

      const response = await cancelSubscription({ subscriptionId });
      if ((response.data as { success: boolean }).success) {
        alert("Subscription canceled successfully.");
        // Redirect or update UI
      } else {
        alert("Failed to cancel subscription.");
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      alert("Error canceling subscription. Please try again.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-0 md:px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-orange-500">Membership Plans</h1>
        <p className="mt-2 text-gray-600">Choose the plan that best fits your comedy writing journey</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Free Plan */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Free</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Get started with the basics</p>
            <p className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">$0</p>
            <p className="text-gray-600 dark:text-gray-300">Forever free</p>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {features.map((feature, index) => (
                <li key={index} className="p-4 flex items-center">
                  {feature.free ? (
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  ) : (
                    <span className="w-5 h-5 mr-3 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${feature.free ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Premium Plan */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden border-2 border-orange-500">
          <div className="p-6">
            <div className="inline-block px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-100 text-xs font-semibold rounded-full mb-4">
              RECOMMENDED
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Premium</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Unlock your full potential</p>
            <p className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">$4.99</p>
            <p className="text-gray-600 dark:text-gray-300">per month</p>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {features.filter(feature => !feature.freeOnly).map((feature, index) => (
                <li key={index} className="p-4 flex items-center">
                  {feature.premium && (
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-900 dark:text-white">
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900">
            {!isPremium && !isAdmin && <UpgradeButton className="w-full" />}
            {(isPremium || isAdmin) && (
              <>
                <div className="text-center text-green-600 dark:text-green-400 font-medium">
                  ✨ You're on Premium!
                </div>
                <button
                  className="mt-4 w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-400 rounded-md transition-colors"
                  onClick={handleCancelSubscription}
                >
                  Cancel Membership
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
